// Owner: account-settings (plan §3.2, §9.3). The account menu behind the member's avatar in the app
// rail: who is signed in, the Account pages and Sign out. shell-nav mounts it around the rail's user
// card (`<AccountMenu trigger={<button className="rail-user">…</button>} />`). It is part of the
// shell, so its few labels live in the small eager catalog `i18n/account-menu.ts` (the account
// area's catalog loads with its screens, plan §12).
import { useNavigate } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { Avatar, type IconName, Menu, type MenuItem } from '@dataroom/ui';
import { signOut } from '../../lib/auth';
import { type MessageKey, t } from '../../lib/i18n';
import { useMe } from '../../lib/queries';
import type { StaticAppPath } from '../../lib/route-paths';
import './account-menu.css';

/** The Account pages (the same as the Account group of the settings rail). */
export const ACCOUNT_MENU_PAGES: ReadonlyArray<{ to: StaticAppPath; icon: IconName; label: MessageKey }> = [
  { to: '/account/profile', icon: 'person', label: 'accountMenu.profile' },
  { to: '/account/preferences', icon: 'sliders', label: 'accountMenu.preferences' },
  { to: '/account/notifications', icon: 'bell', label: 'accountMenu.notifications' },
  { to: '/account/security', icon: 'shield', label: 'accountMenu.security' },
  { to: '/account/connections', icon: 'plug', label: 'accountMenu.connections' },
  { to: '/account/shortcuts', icon: 'keyboard', label: 'accountMenu.shortcuts' },
];

export function AccountMenu({ trigger }: { trigger: ReactElement }) {
  const me = useMe();
  const navigate = useNavigate();
  const items: MenuItem[] = [
    ...ACCOUNT_MENU_PAGES.map((p): MenuItem => ({
      label: t(p.label),
      icon: p.icon,
      onSelect: () => void navigate({ to: p.to }),
    })),
    { type: 'separator' },
    { label: t('accountMenu.signOut'), icon: 'logout', onSelect: () => void signOut() },
  ];
  return (
    <Menu
      label={t('accountMenu.label')}
      placement="top-start"
      width={260}
      header={
        <div className="acct-menu-head" data-testid="account-menu-head">
          <Avatar
            initials={me.member.initials}
            color={me.member.avatarColor}
            size={32}
            name={me.member.name}
          />
          <span className="acct-menu-who">
            <span className="acct-menu-name">{me.member.name}</span>
            <span className="acct-menu-email">{me.member.email}</span>
          </span>
        </div>
      }
      items={items}
      trigger={trigger}
    />
  );
}
